/**
 * Address matching utility for restricted location validation
 * Implements sophisticated fuzzy matching using uFuzzy library
 */

import Fuse from 'fuse.js';
import { ResortInterface } from 'ks-common';

const NORMALIZE_INPUT_REGEX = /[^a-z0-9]/g;
export const MINIMUM_SCORE = 0.34; // 1/3 matching
export const MAX_RESORT_MATCHES = 3;

/**
 * Interface for pre-normalized resort address data
 * Used for multi-key fuzzy matching
 */
export interface NormalizedResortAddress {
  street: string;
  city: string;
  zip: string;
}

/**
 * Normalizes input to handle punctuation and spacing variants
 * This is CRITICAL for catching cases like:
 * - "Mariott's grand vista" → "mariott grand vista"
 * - "Mariott grand vist a" → "mariott grand vist a"
 */
export function normalizeInput(str: string): string {
  return str.toLowerCase().replace(NORMALIZE_INPUT_REGEX, '');
}

export const fuse = new Fuse([''], {
  includeScore: true,
  threshold: 0.18, // Lower = stricter
  ignoreLocation: true,
  minMatchCharLength: 4,
  includeMatches: true,
  findAllMatches: true,
});

/**
 * Fuse instance configured for multi-key resort address matching
 * Matches street, city, and zip independently then combines scores
 */
export const resortFuse = new Fuse<NormalizedResortAddress>([], {
  includeScore: true,
  threshold: 0.3, // More lenient for multi-key matching
  ignoreLocation: true,
  minMatchCharLength: 3,
  keys: [
    { name: 'street', weight: 2 }, // Street most important
    { name: 'city', weight: 1 },
    { name: 'zip', weight: 1.5 }, // ZIP quite specific
  ],
});

// Cache reference to avoid rebuilding Fuse index on every call
let lastResortCollectionRef: NormalizedResortAddress[] | null = null;

/**
 * Checks if a street address matches any banned location
 * @param streetAddress - The street address to validate
 * @param normalizedBannedList - Array of normalized banned location strings
 * @returns The refIndex of the first matching banned location, or null if no match
 */
export function checkRestrictedAddress(
  streetAddress: string,
  normalizedBannedList: string[],
): number | null {
  if (
    !streetAddress ||
    !normalizedBannedList ||
    normalizedBannedList.length === 0
  ) {
    return null;
  }

  fuse.setCollection(normalizedBannedList);
  const results = fuse.search(normalizeInput(streetAddress));

  if (
    results.length > 0 &&
    results.some(result =>
      result?.score != null ? result.score < MINIMUM_SCORE : false,
    )
  ) {
    return results[0].refIndex;
  }

  return null;
}

/**
 * Checks if a vacation home address matches any resort addresses
 * Uses multi-key fuzzy matching to handle partial field inputs
 * @param streetAddress - The street address to check
 * @param city - The city
 * @param zip - The ZIP code
 * @param resortsList - Array of resorts to match against
 * @param normalizedResortAddresses - Optional pre-computed normalized addresses (for performance)
 * @returns Array of matched ResortInterface (up to MAX_RESORT_MATCHES), empty if no matches
 */
export function checkResortAddress(
  streetAddress: string,
  city: string,
  zip: string,
  resortsList: ResortInterface[],
  normalizedResortAddresses?: NormalizedResortAddress[],
): ResortInterface[] {
  if (!resortsList || resortsList.length === 0) {
    return [];
  }

  // At least one field must be provided
  if (!streetAddress && !city && !zip) {
    return [];
  }

  const normalizedAddresses =
    normalizedResortAddresses && normalizedResortAddresses.length > 0
      ? normalizedResortAddresses
      : resortsList.map(resort => ({
          street: normalizeInput(resort.Street || ''),
          city: normalizeInput(resort.City || ''),
          zip: normalizeInput(resort.ZIP || ''),
        }));

  if (normalizedAddresses !== lastResortCollectionRef) {
    resortFuse.setCollection(normalizedAddresses);
    lastResortCollectionRef = normalizedAddresses;
  }

  // Build search query with $and for provided fields
  // This ensures each field is matched independently
  const searchConditions = [];

  const normalizedStreet = normalizeInput(streetAddress);
  if (normalizedStreet) {
    searchConditions.push({ street: normalizedStreet });
  }

  const normalizedCity = normalizeInput(city);
  if (normalizedCity) {
    searchConditions.push({ city: normalizedCity });
  }

  const normalizedZip = normalizeInput(zip);
  if (normalizedZip) {
    searchConditions.push({ zip: normalizedZip });
  }

  if (searchConditions.length === 0) {
    return [];
  }

  // Use $and to require all provided fields to match
  const results =
    searchConditions.length === 1
      ? resortFuse.search(searchConditions[0])
      : resortFuse.search({ $and: searchConditions });

  const matchedResorts = results
    .filter(result => result?.score != null && result.score < MINIMUM_SCORE)
    .slice(0, MAX_RESORT_MATCHES)
    .map(result => resortsList[result.refIndex]);

  return matchedResorts;
}
